import { TEmployeeRenewVisaDocumentStepGroup } from "@/api/entities/employee-renew-visa-document-step-group.entity";
import { useGetRenewVisaDocumentCreationGroupList } from "@/api/services/main/renew-visa";
import { Linkable } from "@/components/elements/Linkable";
import { Table } from "@/components/elements/Table";
import { AddMoreRenewVisaDocumentModal } from "@/features/renew-visa/components/applicationStep/documentCreation/AddMoreRenewVisaDocumentModal";
import { DeleteMoreRenewVisaDocumentBtn } from "@/features/renew-visa/components/applicationStep/documentCreation/DeleteMoreRenewVisaDocumentBtn";
import { DownloadDocumentCreationFileZipBtn } from "@/features/renew-visa/components/applicationStep/documentCreation/DownloadDocumentCreationFileZipBtn";
import { RenewVisaStepCard } from "@/features/renew-visa/components/RenewVisaStepCard";
import { useMakeDocumentCreationColumns } from "@/features/renew-visa/hook/useMakeDocumentCreationColumns";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { useVisible } from "@/hooks/useVisible";
import { divider, state, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { Stack, Typography } from "@mui/material";
import { FC } from "react";
import { useParams } from "react-router-dom";

export type TRenewVisaApplicationStepProps = {
  isSkipStep: boolean;
  stepGroupData: TEmployeeRenewVisaDocumentStepGroup;
  readonly: boolean;
  isExistBasicInfoData: boolean;
};

export const RenewVisaApplicationStep: FC<TRenewVisaApplicationStepProps> = ({ stepGroupData, readonly, isSkipStep, isExistBasicInfoData }) => {
  const { employeeRenewVisaDocumentGroupId } = useParams();

  const addMoreRenewVisaDocumentModal = useVisible();

  const columns = useMakeDocumentCreationColumns(readonly, isSkipStep);

  const { data, isLoading } = useGetRenewVisaDocumentCreationGroupList({ renewVisaDocumentStepGroupId: stepGroupData.id });
  const {
    table,
    rowSelectionKeys: selectedRenewVisaDocumentIds,
    clearSelection,
  } = useSelectionTable({
    columns,
    rows: data ?? [],
    enableRowSelection: !readonly,
    primaryKey: "renewVisaDocumentId",
    pinningColumns: ["employee-company-application", "upload-application", "upload-avatar-file"],
    muiTableHeadCellProps: ({}) => ({
      sx: {
        backgroundColor: `${divider.low}`,
      },
    }),
  });

  return (
    <>
      <RenewVisaStepCard
        isSkip={isSkipStep}
        isCompleted={stepGroupData.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
        title={`ステップ${isExistBasicInfoData ? stepGroupData.masterDocumentStep.stepOrder : stepGroupData.masterDocumentStep.stepOrder - 1}`}
        subTitle={stepGroupData.masterDocumentStep.stepName}
        buttons={<DownloadDocumentCreationFileZipBtn readonly={isSkipStep} stepGroupData={stepGroupData} renewVisaDocumentCreationGroupList={data} />}
      >
        <Typography component="p" variant="body14" mb="16px" color={text.secondary}>
          添付書類は取得に時間がかかる場合があります。余裕を持って手続きを進めましょう。
          <br />
          全てのデータを作成し、「提出形式をまとめてダウンロード（ZIP形式）」ボタンを押すとステップ{isExistBasicInfoData ? 3 : 2}に進みます。
        </Typography>
        <Table
          addBtnText="外国人を追加する"
          table={{ ...table, addNewRow: readonly ? undefined : addMoreRenewVisaDocumentModal.open }}
          isLoading={isLoading}
          maxHeight="45vh"
        />
        <Stack flexDirection="row" justifyContent="flex-end" mt={3}>
          <DeleteMoreRenewVisaDocumentBtn
            renewVisaDocumentGroupId={employeeRenewVisaDocumentGroupId}
            renewVisaDocumentStepGroupId={stepGroupData.id}
            clearSelectedRenewVisaDocument={clearSelection}
            selectedRenewVisaDocumentIds={selectedRenewVisaDocumentIds}
          />
        </Stack>
        <Stack border={`1px solid ${divider.middle}`} borderRadius={rounded.sm} padding={2} mt={3}>
          <Typography variant="body14Bold">顔写真の注意事項</Typography>
          <Typography component="p" variant="body14" mt={1} color={text.secondary}>
            6ヶ月以内の正面を向いた写真を用意してください。画像を直接アップロード、またはURLから提出依頼より本人にアップロードを依頼することもできます。
            <br />
            顔写真の詳細については、
            <Linkable
              component="span"
              sx={{ color: state.link_1, textDecoration: "underline" }}
              href="https://www.moj.go.jp/isa/applications/status/photo_info_00002.html"
              target="_blank"
              width="100%"
            >
              出入国在留管理庁「提出写真の規格」
            </Linkable>
            をご確認ください。
          </Typography>
        </Stack>
      </RenewVisaStepCard>

      <AddMoreRenewVisaDocumentModal
        existingRenewVisaDocumentIds={data?.map((item) => item.renewVisaDocumentId) ?? []}
        existingCompanySpecificIndustryName={
          data?.at(0)?.employee.contractSpecificBusinessClassificationOne?.companySpecificBusinessClassification?.companySpecificIndustry
            ?.companySpecificIndustry
        }
        existingResidenceStatus={data?.at(0)?.employee.residenceStatus?.residenceStatus}
        renewVisaDocumentStepGroupId={stepGroupData.id}
        isOpen={addMoreRenewVisaDocumentModal.visible}
        onClose={addMoreRenewVisaDocumentModal.close}
      />
    </>
  );
};
